import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CircularProgress, DialogActions, DialogContent, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";

//import customized hooks
import useDebouncedInputChange from "../../../hooks/useDebouncedInputChange ";

//import constants
import BASE_URL from "../../../constants/baseUrls";

//import general components
import BaseTextField from "../../common/base-text-field/base-text-field";
import BaseDialog from "../../common/base-dialog/base-dialog";
import BasePagination from "../../common/base-pagination/base-pagination";
import EmptyOrErrorState from "../../common/empty-or-error-state/empty-or-error-state";
import { BaseButton } from "../../common/base-button/base-button";

//import images and icons
import { InfoCircle, SearchNormal1 } from "iconsax-react";

//import styles
import "./old-passengers-modal.scss";

function OldPassengersModal({ open, setOpen, selectedOldPassenger }) {
    //general variables
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    //handle close modal
    const handleClose = () => {
        setOpen(false);
        setSelectedPassenger(null);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("action");
        newSearchParams.delete("index");
        newSearchParams.delete("roomIndex");
        setSearchParams(newSearchParams, { replace: true });
    };

    const [selectedPassenger, setSelectedPassenger] = useState(null);
    const handleSelectedPassenger = passenger => {
        setSelectedPassenger(passenger);
    };

    //search filter
    const [searchInputvalue, setSearchInputValue] = useState("");
    const handleInputChange = event => {
        setPage(1);
        const { value } = event.target;
        setSearchInputValue(value);
        debouncedOnChange(value);
    };
    const debouncedOnChange = useDebouncedInputChange(input => {
        getPassengerList(input, page);
    }, 1000);

    //pagination
    const [passengerList, setPassengerList] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(passengerList?.current_page ?? 1);
    const handlePaginationChange = async (event, value) => {
        setPage(value);
        getPassengerList(searchInputvalue, value);
    };

    //request get passenger list
    const getPassengerList = async (search, page) => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/account/user/passenger/`, {
                params: {
                    search,
                    page,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setPassengerList(response?.data?.data);
        } catch (error) {
            handleClose();
            if (error.response.status === 401) {
                localStorage.clear();
                setSearchParams({ action: "login" }, { replace: true });
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (open) getPassengerList(searchInputvalue, page);
    }, [open]);

    return (
        <BaseDialog title={t("oldPassenger")} open={open} handleClose={handleClose} maxWidth="md" top="0" icon="close">
            <DialogContent dividers={true}>
                <Grid container item xs={12} rowGap={2}>
                    <Grid container item xs={12} md={4}>
                        <BaseTextField
                            label={t("search")}
                            radius="10px"
                            name="search"
                            value={searchInputvalue}
                            setValue={e => handleInputChange(e)}
                            icon={<SearchNormal1 size="20" color="var(--teal-color-18)" />}
                        />
                    </Grid>

                    <Grid container item xs={12} rowGap={2} justifyContent="center">
                        {isLoading ? (
                            <CircularProgress color="inherit" size={30} />
                        ) : (
                            <>
                                {passengerList?.results?.length ? (
                                    passengerList?.results?.map(passengerItem => {
                                        return (
                                            <Grid
                                                className={`old-passengers-modal__passenger ${
                                                    selectedPassenger?.id === passengerItem?.id ? "selected" : ""
                                                }`}
                                                key={passengerItem.id}
                                                container
                                                item
                                                xs={12}
                                                justifyContent="center"
                                                onClick={() => handleSelectedPassenger(passengerItem)}
                                            >
                                                <Grid container item xs={9.5} columnGap={1} alignItems="center">
                                                    <Grid container item xs="auto" direction="column">
                                                        <span className="old-passengers-modal__label">{`${passengerItem?.first_name || " "} ${
                                                            passengerItem?.last_name || " "
                                                        }`}</span>
                                                        <span className="old-passengers-modal__label">
                                                            {passengerItem?.english_first_name} {passengerItem?.english_last_name}
                                                        </span>
                                                    </Grid>
                                                </Grid>
                                                <Grid container item xs={2.5} direction="column">
                                                    <span className="old-passengers-modal__label">{t("nationalCode")}</span>
                                                    <span className="old-passengers-modal__label">{passengerItem?.national_code}</span>
                                                </Grid>
                                            </Grid>
                                        );
                                    })
                                ) : (
                                    <EmptyOrErrorState
                                        title={t("Messages.No Companion Found")}
                                        icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                                    />
                                )}

                                {passengerList?.results?.length && (
                                    <BasePagination
                                        count={passengerList?.total_pages}
                                        page={passengerList?.current_page ?? 1}
                                        handleChange={handlePaginationChange}
                                    />
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container item xs={12} md={4} rowGap={2} mx="auto">
                    <BaseButton
                        text={t("Confirm and Continue")}
                        color="primary"
                        size="medium"
                        radius="radius-10"
                        handleClick={() => {
                            selectedOldPassenger(selectedPassenger, searchParams.get("index"), searchParams.get("roomIndex"));
                            setTimeout(() => {
                                handleClose();
                            }, 0);
                        }}
                    />
                </Grid>
            </DialogActions>
        </BaseDialog>
    );
}

export default OldPassengersModal;
